import {cms_content_path, logger} from '@bitstillery/common/app'
import {debounce, delay} from '@bitstillery/common/lib/utils'

import {claim_hydrated_cms_page, ensure_page_copy, load_page} from '@/cms/runtime/loader'
import {cms_published_path_from_pathname, portal_path_is_published_cms_route} from '@/cms/lib/route_utils'
import {warm_cms_page} from '@/cms/runtime/warmup'

/**
 * Get a CMS page ready before the visitor asks for it.
 *
 * A cold CMS navigation — /shop → /, where nothing of the CMS world is cached yet — has to fetch the
 * page module, fetch the page's copy, compile the module, and then load a source per `AsyncData` block
 * before the page is worth looking at. Done at click time that is a blank `<main>` followed by five
 * reflows; done in advance it is invisible. A repeat of the same navigation is smooth for exactly this
 * reason — everything is already cached, so the page commits synchronously and renders in one pass.
 *
 * Pointing at, tabbing to or touching a link is a reliable head start on the click, and the home page
 * (linked from the logo and breadcrumb on every surface, including the non-CMS ones that never warm it)
 * is worth an idle slot on its own. What none of it changes is what the visitor is looking at: the page
 * being readied lands in the caches and, while its data loads, renders only on a staging node out of
 * sight (see `runtime/warmup`). It does put its own copy in the i18next bundle first, so that staged
 * render resolves real strings — minus the hero's two keys, which describe the page still on screen and
 * are the commit's to swap, together with `$s.page`.
 */

/**
 * Hovering across a menu sweeps every link in it. Waiting out a short rest on one link keeps that from
 * turning into a request per link, and still leaves the bulk of a real hover as head start.
 */
const INTENT_DELAY = 65

/**
 * Longest the route resolver holds the outgoing page while this runs. `warm_cms_page` caps its own wait
 * for data, but the fetches before it have no timeout of their own, and this is the layer that keeps the
 * UI hostage — so the bound belongs here. Whatever is still in flight keeps filling the caches.
 */
const READY_DEADLINE = 1500

/** Verdict per pathname: `portal_path_is_published_cms_route` recompiles every route template per call. */
const is_cms_link = new Map<string, boolean>()

async function load_everything(page_path: string): Promise<void> {
    let loaded
    try {
        // The module and the copy are independent. `ensure_page_copy` leaves the hero copy out, so the
        // page still on screen keeps its title — and the staged render below still resolves real strings.
        ;[loaded] = await Promise.all([load_page(page_path), ensure_page_copy(page_path)])
    } catch (error) {
        logger.debug(`[cms prefetch] ${page_path} failed: ${error instanceof Error ? error.message : String(error)}`)
        return
    }
    if (!loaded.component) {
        return
    }
    await warm_cms_page(page_path, loaded.component)
}

/**
 * Everything `path`'s page needs before it can be shown in one piece: module compiled, copy fetched,
 * data and images loaded. Best-effort and bounded — whatever is missing when this resolves, the
 * navigation simply loads itself, the way it always did.
 *
 * Called from three places, in descending order of how much is left to do: on intent (hover/focus/touch)
 * and on idle, where it is free because nobody is waiting; and from the route resolver, which awaits it
 * with the previous page still on screen. Once a page has been made ready the later calls fall through,
 * which is the point — the click should find the work already done.
 */
export async function ready_cms_page(path: string): Promise<void> {
    if (globalThis.__SSR_MODE__) {
        return
    }
    // A route path carries the query and hash; the page is keyed by pathname alone, and overlay routes
    // (/login, /redeem, …) resolve to the home page rendered behind them.
    const page_path = cms_content_path(cms_published_path_from_pathname(path))
    // The page this tab hydrated is on screen already and warm from the SSR handoff; staging it would
    // hold up its own hydration for nothing.
    if (claim_hydrated_cms_page(page_path)) {
        return
    }
    await Promise.race([load_everything(page_path), delay(READY_DEADLINE, null)])
}

/** Where `anchor` leads, if that is a CMS page this tab can usefully warm — else `null`. */
function cms_path_of(anchor: HTMLAnchorElement): string | null {
    if (anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) {
        return null
    }
    const href = anchor.getAttribute('href')
    if (!href || href.startsWith('#')) {
        return null
    }
    let url: URL
    try {
        url = new URL(anchor.href, window.location.origin)
    } catch {
        return null
    }
    // Rules out other origins, and mailto:/tel: with them (their `origin` is 'null').
    if (url.origin !== window.location.origin) {
        return null
    }
    let verdict = is_cms_link.get(url.pathname)
    if (verdict === undefined) {
        verdict = portal_path_is_published_cms_route(url.pathname)
        is_cms_link.set(url.pathname, verdict)
    }
    return verdict ? url.pathname : null
}

/**
 * Ready the CMS page behind a link as soon as the visitor points at, tabs to or touches it. Delegated
 * from the window rather than wired per component, so the menu, breadcrumbs, logo, footer and whatever a
 * CMS page itself renders are all covered without a prop for it — and a link to a page already loaded
 * costs nothing, so hovering the page you are on is free.
 */
export function ready_cms_pages_on_intent(): void {
    if (globalThis.__SSR_MODE__) {
        return
    }
    const ready_on_rest = debounce(INTENT_DELAY, ready_cms_page)
    const on_intent = (event: Event) => {
        const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
        const path = anchor instanceof HTMLAnchorElement ? cms_path_of(anchor) : null
        if (path) {
            void ready_on_rest(path)
        }
    }
    for (const event of ['pointerover', 'focusin', 'touchstart']) {
        window.addEventListener(event, on_intent, {passive: true})
    }
}

/** Ready `path` once the browser is done with the work the visitor is actually waiting for. */
export function ready_cms_page_when_idle(path: string): void {
    if (globalThis.__SSR_MODE__) {
        return
    }
    const ready = () => void ready_cms_page(path)
    if (typeof globalThis.requestIdleCallback === 'function') {
        globalThis.requestIdleCallback(ready, {timeout: 3000})
    } else {
        globalThis.setTimeout(ready, 1000)
    }
}
